/**
 * What happened, and what to do about it.
 *
 * The API answers with RFC 9457 problem documents, so `ApiError.message` is
 * already the server's own sentence — printing it beats any generic apology we
 * could write. The status only changes the framing.
 */
import type { ReactNode } from 'react'
import { CircleAlertIcon, RotateCwIcon, WifiOffIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ApiError } from '@/lib/api'
import { cn } from '@/lib/utils'

export interface ErrorStateProps {
  /** Whatever the query or mutation threw. */
  error: unknown
  /** Wire this to `refetch`. Omit for errors that retrying will not fix. */
  onRetry?: () => void
  /** Overrides the generated heading. */
  title?: string
  size?: 'inline' | 'page'
  action?: ReactNode
  className?: string
}

export function ErrorState({
  error,
  onRetry,
  title,
  size = 'inline',
  action,
  className,
}: ErrorStateProps) {
  const offline = error instanceof ApiError && error.status === 0
  const Icon = offline ? WifiOffIcon : CircleAlertIcon

  return (
    <div
      role="alert"
      className={cn(
        'border-rose/30 bg-rose/5 flex flex-col items-center justify-center rounded-lg border text-center',
        size === 'page' ? 'gap-3 px-6 py-16' : 'gap-2 px-4 py-10',
        className,
      )}
    >
      <Icon className={cn('text-rose', size === 'page' ? 'size-8' : 'size-6')} aria-hidden />
      <h3 className={cn('font-display font-medium', size === 'page' ? 'text-lg' : 'text-sm')}>
        {title ?? headingFor(error)}
      </h3>
      <p className="text-dim max-w-md text-sm text-balance">{messageFor(error)}</p>
      {(onRetry || action) && (
        <div className="mt-1 flex items-center gap-2">
          {onRetry && (
            <Button variant="outline" size="sm" onClick={onRetry}>
              <RotateCwIcon aria-hidden />
              Try again
            </Button>
          )}
          {action}
        </div>
      )}
    </div>
  )
}

function headingFor(error: unknown): string {
  if (!(error instanceof ApiError)) return 'Something went wrong'
  if (error.status === 0) return 'Could not reach the server'
  if (error.status === 404) return 'Not found'
  if (error.status === 403) return 'Not allowed'
  if (error.status === 429) return 'Too many requests'
  if (error.status >= 500) return 'The server had a problem'
  return 'That request failed'
}

function messageFor(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 0) {
      return 'The app could not reach the API. Check your connection, then try again.'
    }
    if (error.status >= 500) {
      return `${error.message} This is a server-side fault — retrying in a moment usually works.`
    }
    return error.message
  }
  if (error instanceof Error) return error.message
  return 'An unexpected error occurred.'
}
